import JobApi from '@/api-interface/companyjob'
import { isEmpty, orderBy, groupBy, debounce } from 'lodash-es'
import Numeral from 'numeral'
import Vue from 'vue'
import statusList from '@/lookup-data/company-job-status-list'

export default {
  namespaced: true,

  getters: {
    /**
     * Returns a function that returns the latest GPS location for a given companyJobKey. Output is in the format of
     * `{ lat: Number, lng: Number }`. If no location is available, `undefined` is returned
     * @param state
     * @returns {Function} function that accepts a companyJobKey
     */
    lastGpsEntry(state) {
      return function(companyJobKey) {
        // because the execution log is sorted from most-to-least recent (aka: newest on top), we can just look for
        // one GPS entry key and feel confident that that is the most recent
        if (state.executionLogs.hasOwnProperty(companyJobKey)) {
          const lastGpsEntry = state.executionLogs[companyJobKey].find(
            (log) => log.companyJobExecutionLogEntryKey === 'gps_location'
          )

          if (lastGpsEntry) {
            const [lat, lng] = lastGpsEntry.companyJobExecutionLogEntryValue.split(',')
            return {
              lat: Number.parseFloat(lat),
              lng: Number.parseFloat(lng)
            }
          }
        }
      }
    },

    /**
     * Array of transactions that have not yet been picked up (but have been awarded)
     * Basically a subset of `isInProgress`
     * @param state
     * @returns {T[]}
     */
    toBePickedUp(state) {
      return state.list.filter(
        (job) => isEmpty(job.companyJobStatus) || statusList.to_be_pickedup.includes(job.companyJobStatus.toLowerCase())
      )
    },

    /**
     * Array of transactions that have are to be dropped off (aka: in transit)
     * Basically a subset of `isInProgress`
     *
     * @param state
     * @returns {T[]}
     */
    inTransit(state) {
      return state.list.filter(
        (job) => isEmpty(job.companyJobStatus) || statusList.in_transit.includes(job.companyJobStatus.toLowerCase())
      )
    },

    /**
     * Array of transactions that have been dropped off.
     * Basically a subset of `isComplete`
     *
     * @param state
     * @returns {T[]}
     */
    droppedOff(state) {
      return state.list.filter(
        (job) => isEmpty(job.companyJobStatus) || statusList.droppedoff.includes(job.companyJobStatus.toLowerCase())
      )
    },

    /**
     * Array of transactions that are in a draft state
     * @param state
     * @returns {T[]}
     */
    isDraft(state) {
      return state.list.filter(
        (job) => isEmpty(job.companyJobStatus) || statusList.draft.includes(job.companyJobStatus.toLowerCase())
      )
    },

    /**
     * Array of transactions that are in an in-transaction state
     * @param state
     * @returns {T[]}
     */
    isInProgress(state) {
      return state.list.filter(
        (job) => !isEmpty(job.companyJobStatus) && statusList.active.includes(job.companyJobStatus.toLowerCase())
      )
    },

    /**
     * Array of transactions that are in a completed state
     * @param state
     * @returns {T[]}
     */
    isComplete(state) {
      return state.list.filter(
        (job) => !isEmpty(job.companyJobStatus) && statusList.complete.includes(job.companyJobStatus.toLowerCase())
      )
    },

    isPendingPayment(state) {
      return state.list.filter(
        (job) =>
          !isEmpty(job.companyJobStatus) && statusList.payment_pending.includes(job.companyJobStatus.toLowerCase())
      )
    },

    isPaymentApproved(state) {
      return state.list.filter(
        (job) =>
          !isEmpty(job.companyJobStatus) && statusList.payment_approved.includes(job.companyJobStatus.toLowerCase())
      )
    },

    isShipperApprovalPending(state) {
      return state.list.filter(
        (job) =>
          !isEmpty(job.companyJobStatus) &&
          statusList.shipper_approval_pending.includes(job.companyJobStatus.toLowerCase())
      )
    },

    /**
     * Same data as `state.list` but has a hashmap of `companyJobKey` for quick lookups
     * @param state
     * @returns {*}
     */
    listAsMap(state) {
      return state.list.reduce((aggregate, current) => {
        aggregate[current.companyJobKey] = current
        return aggregate
      }, {})
    }
  },

  state: {
    list: [],
    executionLogs: {}
  },

  mutations: {
    FETCH_LIST(state, list) {
      state.list = orderBy(list, 'updatedTimestamp', 'desc')
    },

    DELETE_JOB(state, companyJobKey) {
      const DELETE_COUNT = 1
      const targetIndex = state.list.findIndex((job) => job.companyJobKey === companyJobKey)
      state.list.splice(targetIndex, DELETE_COUNT)
    },

    SET_JOB_LOG(state, { jobId, details }) {
      let logEntries = details.map((det) => {
        return {
          ...det,
          companyJobExecutionLogEntryTimestamp: det.companyJobExecutionLogEntryTimestamp || ''
        }
      })
      // sort log entries so that most recent is AT THE TOP (aka: first) in the list
      logEntries = orderBy(logEntries, 'companyJobExecutionLogEntryTimestamp', 'desc')
      Vue.set(state.executionLogs, jobId, logEntries)
    }
  },

  actions: {
    /**
     * Fetch all transactions and fully populate their jobPoints
     *
     * @param commit
     * @param factoringProviderKey (optional)
     * @returns {Promise<void>}
     */
    async fetch(
      { commit, rootState },
      { factoringProviderKey = null, createdByAccountKey = rootState.filterDashboardByUser, accountKey = null } = {}
    ) {
      const response = await JobApi.list({ factoringProviderKey, createdByAccountKey, accountKey })
      const populated = response.map((job) => {
        const points = job.companyJobPoints

        const defaultLocationObject = {
          companyRolodexJobLocation: {}
        }

        let origins = points.filter((point) => point.companyJobPointType === 'origin') || [defaultLocationObject]
        let destinations = points.filter((point) => point.companyJobPointType === 'destination') || [
          defaultLocationObject
        ]

        return {
          ...job,
          companyJobRateFormatted: Numeral(job.companyJobRate).format('$0,0.00'),
          origins,
          destinations
        }
      })

      commit('FETCH_LIST', populated)
    },

    /**
     * Fetch the execution log for all in-progress transactions.
     * This action is rate-limited and will only execute max once every 5 seconds
     *
     * @param commit
     * @param getters
     * @returns {Promise<void>}
     */
    fetchExecutionLogForInProgress: debounce(async function fetchExecutionLogForInProgress(
      { commit, dispatch, rootGetters },
      { factoringProviderKey, createdByAccountKey, accountKey } = {}
    ) {
      await dispatch('fetch', { factoringProviderKey, createdByAccountKey, accountKey })

      let companyKey = null
      if (rootGetters['user/isShipper']) {
        companyKey = rootGetters['user/accountInfo'].company.companyKey
      }

      try {
        let executionLog = await JobApi.getExecutionLog({ companyKey, factoringProviderKey, accountKey })
        executionLog = groupBy(executionLog, 'companyJobKey')

        Object.entries(executionLog).forEach(([companyJobKey, logs]) => {
          commit('SET_JOB_LOG', { jobId: companyJobKey, details: logs })
        })
      } catch (error) {
        console.error('failed to fetch execution log', error)
        return
      }
    },
    5000),

    async fetchExecutionLogByKey({ commit }, companyJobKey) {
      const logs = await JobApi.getExecutionLog({ companyJobKey: companyJobKey })

      commit('SET_JOB_LOG', { jobId: companyJobKey, details: logs })
    },

    async deleteByCompanyJobKey({ commit }, companyJobKey) {
      await JobApi.delete(companyJobKey)
      commit('DELETE_JOB', companyJobKey)
    },

    async completeShipperReview({ getters }, { isApproved, companyJobKeys }) {
      await Promise.all(
        companyJobKeys.map((key) => {
          const fullCompanyJob = Object.assign({}, getters.listAsMap[key])
          fullCompanyJob.companyJobShipperVerifyCompleteFlag = isApproved
          // fullCompanyJob.companyJobShipperVerifyCompleteAccountKey
          return JobApi.update(fullCompanyJob)
        })
      )
    },

    async completeFactoringReview({ getters }, { isApproved, companyJobKeys }) {
      await Promise.all(
        companyJobKeys.map((key) => {
          const fullCompanyJob = Object.assign({}, getters.listAsMap[key])
          fullCompanyJob.companyJobFactoringInvoiceApprovedFlag = isApproved
          // fullCompanyJob.companyJobFactoringInvoiceApprovedByAccountKey
          return JobApi.update(fullCompanyJob)
        })
      )
    }
  }
}
